import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Users, MessageCircle, Heart, Share2, ThumbsUp, Send, 
  Sparkles, ArrowRight, Zap, Clock, User, Lock, Star,
  MessageSquare, BookOpen, Lightbulb, HelpCircle
} from 'lucide-react';

// Sample discussion topics/categories
const discussionCategories = [
  {
    icon: Lightbulb,
    title: 'Career Advice',
    description: 'Get guidance on career decisions, transitions, and growth strategies',
    posts: 47,
    color: 'purple'
  },
  {
    icon: MessageSquare,
    title: 'Professional Communication',
    description: 'Tips and discussions on workplace communication',
    posts: 32,
    color: 'teal'
  },
  {
    icon: BookOpen,
    title: 'Learning & Development',
    description: 'Share resources, courses, and learning experiences',
    posts: 28,
    color: 'purple'
  },
  {
    icon: HelpCircle,
    title: 'Q&A',
    description: 'Ask questions and get answers from the community',
    posts: 56,
    color: 'teal'
  },
  {
    icon: Heart,
    title: 'Support & Encouragement',
    description: 'A safe space to share challenges and receive support',
    posts: 41,
    color: 'purple'
  },
  {
    icon: Users,
    title: 'Networking',
    description: 'Connect with professionals in your field',
    posts: 23,
    color: 'teal'
  },
];

// Sample featured discussions
const featuredDiscussions = [
  {
    id: 1,
    author: 'Sarah M.',
    avatar: 'SM',
    title: 'How do you handle difficult conversations with your manager?',
    excerpt: 'I have a performance review coming up and want to discuss my career growth. Any tips on how to approach this conversation professionally?',
    category: 'Career Advice',
    likes: 24,
    replies: 12,
    time: '2 hours ago',
    isHot: true
  },
  {
    id: 2,
    author: 'Michael T.',
    avatar: 'MT',
    title: 'Transitioning from healthcare to HR - my journey',
    excerpt: 'After 10 years as a nurse, I made the leap to HR. Here\'s what I learned about transferable skills and how to position yourself for a career change.',
    category: 'Career Advice',
    likes: 45,
    replies: 18,
    time: '5 hours ago',
    isHot: true
  },
  {
    id: 3,
    author: 'Joyce C.',
    avatar: 'JC',
    title: 'Weekly Tip: Setting boundaries in remote work',
    excerpt: 'When working from home, it\'s easy to let work bleed into personal time. Here are my top 3 strategies for maintaining work-life balance...',
    category: 'Professional Communication',
    likes: 67,
    replies: 23,
    time: '1 day ago',
    isHot: false,
    isAdmin: true
  },
];

// Discussion Card Component
const DiscussionCard = ({ discussion }) => {
  return (
    <Card className="group border border-stone-100 hover:border-hr-purple/30 shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
            discussion.isAdmin ? 'bg-hr-purple text-white' : 'bg-hr-purple-light text-hr-purple'
          }`}>
            <span className="font-bold text-sm">{discussion.avatar}</span>
          </div>
          
          {/* Content */}
          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-hr-text-primary">{discussion.author}</span>
              {discussion.isAdmin && (
                <span className="text-xs bg-hr-purple text-white px-2 py-0.5 rounded-full">Admin</span>
              )}
              <span className="text-xs text-hr-text-muted">• {discussion.time}</span>
              {discussion.isHot && (
                <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Zap className="h-3 w-3" /> Hot
                </span>
              )}
            </div>
            
            <h3 className="font-heading text-lg font-semibold text-hr-text-primary mb-2 group-hover:text-hr-purple transition-colors duration-300">
              {discussion.title}
            </h3>
            
            <p className="text-sm text-hr-text-secondary line-clamp-2 mb-4">
              {discussion.excerpt}
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-hr-teal bg-hr-teal-light px-3 py-1 rounded-full">
                {discussion.category}
              </span>
              <div className="flex items-center gap-4 text-hr-text-muted text-sm">
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4" /> {discussion.likes}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="h-4 w-4" /> {discussion.replies}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const Community = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/20 relative" data-testid="community-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <Users className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Connect & Grow</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              Join the <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Community</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Connect with like-minded professionals, ask questions, share insights, and grow together. This is your space to learn, support, and be supported.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
              <Link to="/membership" data-testid="community-join-btn">
                <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-10 py-7 text-lg shadow-2xl hover:shadow-hr-purple/40 transition-all duration-500 hover:-translate-y-2 btn-3d">
                  <Lock className="mr-2 h-5 w-5" />
                  Join to Participate
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discussion Categories */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="community-categories">
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-4">
              <MessageSquare className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Discussion Topics</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              Explore Topics
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Find conversations that matter to you
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {discussionCategories.map((category, index) => (
              <Card 
                key={index}
                className="group border border-stone-100 hover:border-hr-purple/30 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
              >
                <CardContent className="p-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-hr-purple/5 to-hr-teal/5 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-md group-hover:shadow-lg ${
                    category.color === 'purple' ? 'bg-gradient-to-br from-hr-purple-light to-hr-purple/20' : 'bg-gradient-to-br from-hr-teal-light to-hr-teal/20'
                  }`}>
                    <category.icon className={`h-7 w-7 ${
                      category.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'
                    }`} />
                  </div>
                  
                  <h3 className="relative font-heading text-lg font-semibold text-hr-text-primary mb-2 group-hover:text-hr-purple transition-colors duration-300">
                    {category.title}
                  </h3>
                  <p className="relative text-sm text-hr-text-muted mb-4">
                    {category.description}
                  </p>
                  <div className="relative flex items-center justify-between">
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                      category.color === 'purple' ? 'bg-hr-purple/10 text-hr-purple' : 'bg-hr-teal/10 text-hr-teal'
                    }`}>
                      {category.posts} discussions
                    </span>
                    <ArrowRight className="h-4 w-4 text-hr-text-muted group-hover:text-hr-purple group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  
                  <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full bg-gradient-to-r from-hr-purple to-hr-teal transition-all duration-500" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Discussions */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg to-white relative overflow-hidden" data-testid="community-featured">
        <div className="absolute top-20 left-10 w-40 h-40 bg-hr-teal/10 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-hr-purple/10 rounded-full blur-3xl animate-float-slow" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-4">
              <Zap className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Trending Now</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              Featured Discussions
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Join the conversation on popular topics
            </p>
          </div>

          <div className="space-y-6">
            {featuredDiscussions.map((discussion, index) => (
              <div 
                key={discussion.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
              >
                <DiscussionCard discussion={discussion} />
              </div>
            ))}
          </div>

          {/* Login to participate notice */}
          <div className="mt-12 p-8 bg-gradient-to-r from-hr-purple/10 to-hr-teal/10 rounded-3xl text-center animate-fade-in-up stagger-4" style={{ opacity: 0 }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lock className="h-6 w-6 text-hr-purple" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mb-2">
              Want to Join the Conversation?
            </h3>
            <p className="text-hr-text-secondary mb-6">
              Become a member to post questions, share insights, and connect with the HR Nest community.
            </p>
            <Link to="/membership">
              <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105">
                View Membership Options
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Community Stats */}
      <section className="py-20 bg-gradient-to-br from-hr-purple via-hr-purple-dark to-hr-purple relative overflow-hidden" data-testid="community-stats">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-blob" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="animate-fade-in-up" style={{ opacity: 0 }}>
              <div className="text-5xl font-bold text-white mb-2">500+</div>
              <div className="text-white/70">Community Members</div>
            </div>
            <div className="animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              <div className="text-5xl font-bold text-white mb-2">200+</div>
              <div className="text-white/70">Active Discussions</div>
            </div>
            <div className="animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
              <div className="text-5xl font-bold text-white mb-2">1,500+</div>
              <div className="text-white/70">Helpful Replies</div>
            </div>
            <div className="animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
              <div className="text-5xl font-bold text-white mb-2">50+</div>
              <div className="text-white/70">Expert Contributors</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="community-cta">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6">
            <Star className="h-4 w-4 text-hr-teal animate-pulse" />
            <span className="text-hr-teal text-sm font-medium">Start Today</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary mb-6 animate-fade-in-up">
            Ready to Connect?
          </h2>
          <p className="text-hr-text-secondary text-lg mb-12 max-w-2xl mx-auto animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
            Join the HR Nest community and get access to discussions, resources, and a supportive network that will help you thrive in your career.
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
            <Link to="/membership" data-testid="community-final-cta-btn">
              <Button className="group bg-hr-teal hover:bg-hr-teal-dark text-white rounded-full px-10 py-7 text-lg font-medium shadow-2xl hover:shadow-hr-teal/30 transition-all duration-500 hover:-translate-y-3 hover:scale-105">
                Become a Member
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-hr-purple text-hr-purple hover:bg-hr-purple hover:text-white rounded-full px-10 py-7 text-lg font-medium transition-all duration-500 hover:-translate-y-3 hover:scale-105">
                Have Questions?
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Community;
